import React from "react";
import { Check } from "lucide-react";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type FeatureRow =
  | { type: "category"; label: string }
  | {
      type: "feature";
      feature: string;
      tooltip?: string;
      free: string | boolean;
      pro: string | boolean;
      team: string | boolean;
    };

const FEATURES: FeatureRow[] = [
  // ── Content ──
  { type: "category", label: "Content" },
  {
    type: "feature",
    feature: "Adding content",
    tooltip: "Upload PDFs, videos, audio, images, or paste text to study from.",
    free: "3 / day",
    pro: "Unlimited",
    team: "Unlimited",
  },
  {
    type: "feature",
    feature: "Document page limit",
    free: "100 pages",
    pro: "2,000 pages",
    team: "5,000 pages",
  },
  {
    type: "feature",
    feature: "Document file size",
    free: "10 MB",
    pro: "300 MB",
    team: "500 MB",
  },

  // ── Chat ──
  { type: "category", label: "Chat" },
  {
    type: "feature",
    feature: "AI chat messages",
    tooltip: "Messages sent to the AI tutor per day.",
    free: "5 / day",
    pro: "100 / day",
    team: "100 / day",
  },
  {
    type: "feature",
    feature: "AI model access",
    tooltip: "Free uses a standard model. Paid plans unlock advanced models.",
    free: "Standard (Auto)",
    pro: "Advanced models",
    team: "Advanced models",
  },

  // ── Study tools ──
  { type: "category", label: "Study tools" },
  {
    type: "feature",
    feature: "Flashcards",
    tooltip: "AI-generated flashcard decks from your content.",
    free: "10 / day",
    pro: "Unlimited",
    team: "Unlimited",
  },
  {
    type: "feature",
    feature: "Summaries",
    tooltip: "AI-generated summaries of your uploaded content.",
    free: "5 / day",
    pro: "Unlimited",
    team: "Unlimited",
  },
  {
    type: "feature",
    feature: "Quiz answers",
    tooltip: "Number of quiz questions you can answer per day.",
    free: "10 / day",
    pro: "Unlimited",
    team: "Unlimited",
  },
  {
    type: "feature",
    feature: "Practice exams",
    tooltip: "Full-length practice exams generated from your material.",
    free: "2 / month",
    pro: "Unlimited",
    team: "Unlimited",
  },
  {
    type: "feature",
    feature: "Mind maps",
    tooltip: "Visual mind map diagrams generated from your content.",
    free: "3 / day",
    pro: "Unlimited",
    team: "Unlimited",
  },
  {
    type: "feature",
    feature: "Flow charts",
    tooltip: "Process and flow diagrams generated from your content.",
    free: "3 / day",
    pro: "Unlimited",
    team: "Unlimited",
  },
  {
    type: "feature",
    feature: "Timelines",
    tooltip: "Chronological timeline visuals from your content.",
    free: "3 / day",
    pro: "Unlimited",
    team: "Unlimited",
  },
  {
    type: "feature",
    feature: "Charts (Bar, Line, Pie)",
    tooltip: "Data visualization charts generated from your content.",
    free: "3 / day",
    pro: "Unlimited",
    team: "Unlimited",
  },
  {
    type: "feature",
    feature: "Venn diagrams",
    tooltip: "Compare and contrast diagrams from your content.",
    free: "3 / day",
    pro: "Unlimited",
    team: "Unlimited",
  },

  // ── Support ──
  { type: "category", label: "Support" },
  {
    type: "feature",
    feature: "Priority customer support",
    free: false,
    pro: false,
    team: true,
  },

  // ── Collaboration ──
  { type: "category", label: "Collaboration" },
  {
    type: "feature",
    feature: "Team & shared spaces",
    tooltip: "Collaborate with teammates in shared rooms with centralized billing.",
    free: false,
    pro: "Teams add-on",
    team: true,
  },
];

function CellValue({ value }: { value: string | boolean }) {
  if (value === true) {
    return <Check className="mx-auto h-4 w-4 text-primary" aria-label="Included" />;
  }
  if (value === false) {
    return <span className="text-muted-foreground">—</span>;
  }
  return <>{value}</>;
}

function FeatureLabel({ feature, tooltip }: { feature: string; tooltip?: string }) {
  if (!tooltip) {
    return <span>{feature}</span>;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button className="cursor-help underline decoration-muted-foreground/50 decoration-dashed underline-offset-2">
          {feature}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

export function PlansFeatureTable() {
  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="w-[220px] text-xs font-medium uppercase text-muted-foreground">
            Feature
          </TableHead>
          <TableHead className="text-center">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Free
            </span>
          </TableHead>
          <TableHead className="text-center">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Pro
            </span>
          </TableHead>
          <TableHead className="text-center">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Team
            </span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {FEATURES.map((row, i) => {
          if (row.type === "category") {
            return (
              <TableRow key={i} className="hover:bg-transparent">
                <TableCell
                  colSpan={4}
                  className="bg-muted/30 py-2 text-xs font-medium text-muted-foreground sm:text-sm"
                >
                  {row.label}
                </TableCell>
              </TableRow>
            );
          }

          return (
            <TableRow key={i} className="hover:bg-transparent">
              <TableCell className="py-2 pl-4 text-xs font-medium sm:text-sm">
                <FeatureLabel feature={row.feature} tooltip={row.tooltip} />
              </TableCell>
              <TableCell className="py-2 text-center text-xs sm:text-sm">
                <CellValue value={row.free} />
              </TableCell>
              <TableCell className="py-2 text-center text-xs sm:text-sm">
                <CellValue value={row.pro} />
              </TableCell>
              <TableCell className="py-2 text-center text-xs sm:text-sm">
                <CellValue value={row.team} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
